import { Component, OnInit, Input } from '@angular/core';
import {
  FormGroup,
  ControlContainer,
  FormControl,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.css'],
})
export class DatePickerComponent implements OnInit {
  public formGroup: FormGroup;

  @Input() dueDate: Date;
  @Input() initialDate: Date;
  @Input() minDateInput: string;
  @Input() maxDateInput: string;
  @Input() title: string;
  @Input() placeholder: string;
  @Input() showError: boolean;
  @Input() formName: string;
  @Input() resetDateForm: () => void;
  @Input() isDateResettable: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() resetButtonText: string;
  @Input() customErrorMessage: string;

  showCalendar: boolean;
  minDate: Date;
  maxDate: Date;
  dateFormat: string;
  errorMessage: string;
  clearedForm: boolean;

  constructor(private controlContainer: ControlContainer) {
    this.dateFormat = 'yyyy-MM-dd';
  }

  ngOnInit(): void {
    this.showCalendar = false;
    this.dueDate = this.initialDate ?? new Date();
    this.setup();
  }

  setup() {
    if (this.clearedForm) {
      this.dueDate = this.initialDate ?? new Date();
    }
    this.clearedForm = false;
    this.formGroup = <FormGroup>this.controlContainer.control;
    const setMinDateHours = new Date(this.minDateInput).setHours(12, 0, 0, 0);
    const setMaxDateHours = new Date(this.maxDateInput).setHours(12, 0, 0, 0);
    this.minDate = new Date(setMinDateHours);
    this.maxDate = new Date(setMaxDateHours);

    this.formGroup.addControl(
      this.formName,
      new FormControl(formatDate(this.dueDate, this.dateFormat, 'en'), [
        Validators.required,
        this.validateDateRange(this.minDate, this.maxDate),
        Validators.maxLength(10),
        this.validdateDateFormat(),
      ])
    );
  }

  convertDEStringToSEDate(dateString: string): Date {
    const parts = dateString.split('.');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    } else {
      return new Date(dateString);
    }
  }

  convertSEStringToDEString(dateString): string {
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month =
        parseInt(parts[1], 10) <= 10
          ? parseInt(parts[1], 10)
          : parseInt(parts[1], 10);
      const day =
        parseInt(parts[2], 10) <= 10
          ? '0' + parseInt(parts[2], 10)
          : parseInt(parts[2], 10);
      return day + '.' + month + '.' + year;
    } else {
      return;
    }
  }

  clearForm = (): void => {
    this.clearedForm = true;
    this.resetDateForm();
  };

  closeCalendar = (): void => {
    this.showCalendar = false;
    if (this.clearedForm) {
      this.formGroup.controls[this.formName].setValue('');
    } else {
      this.formGroup.controls[this.formName].setValue(
        formatDate(this.dueDate, this.dateFormat, 'en')
      );
      this.setup();
    }
  };

  onDateChange(newValue: Date): void {
    this.dueDate = newValue;
  }

  onDateInputChange(): void {
    this.errorMessage = '';
    let inputValue = this.formGroup.controls[this.formName].value;
    const inputValueDate: Date = new Date(inputValue);

    //If inputValueDate is a valid Date it returns a number
    if (!isNaN(inputValueDate.getTime())) {
      if (this.dueDate === null) {
        this.errorMessage = '';
      } else if (inputValueDate < this.minDate) {
        this.errorMessage = `Please enter a date from ${this.minDateInput}`;
      } else if (inputValueDate > this.maxDate) {
        this.errorMessage = `Please enter a date until ${this.maxDateInput}`;
      }

      if (
        this.dueDate !== null &&
        inputValueDate &&
        this.formGroup.controls[this.formName].valid
      ) {
        this.dueDate = inputValueDate;
        this.errorMessage = '';
      }
    } else {
      this.errorMessage = `Please enter a date format according to yyyy-mm-dd`;
    }
  }

  onClickSelect(): void {
    if (this.isDisabled) {
      return;
    }

    this.setup();
    this.showCalendar = !this.showCalendar;
  }

  validdateDateFormat(): ValidatorFn {
    const DATE_FORMAT_YYYMMDD_SE: RegExp =
      /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;

    return (control: AbstractControl): { [key: string]: any } => {
      const input: string = control.value;

      if (!input) {
        return;
      }

      return DATE_FORMAT_YYYMMDD_SE.test(input) ? null : { wrongFormat: true };
    };
  }

  validateDateRange(min: Date, max: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const error: ValidationErrors = { integer: true };
      const value: number = new Date(control.value).setHours(12, 0, 0, 0);
      if (min.getTime() > value || max.getTime() < value) {
        return error;
      }

      return null;
    };
  }

  getErrorMessage(): string {
    const errors = this.formGroup.controls[this.formName].errors;

    if (!errors) {
      return null;
    }

    if (errors['invalidDateRange']) {
      return this.customErrorMessage;
    }

    return this.errorMessage;
  }
}
